import { useState, type ReactNode } from 'react';
import { Box, Tabs, Tab, Chip, Stack, Typography, Paper } from '@mui/material';
import type { InspectorData } from '@replayops/replay-core';
import { useT } from '../i18n/useT.js';

const LEVEL_COLOR: Record<string, string> = { error: '#ef4444', warn: '#f59e0b', info: '#3b82f6', log: '#6b7280', debug: '#6b7280' };
function statusColor(s: number): string {
  if (s === 0) return '#ef4444';
  if (s < 300) return '#2ec2b3';
  if (s < 400) return '#0ea5e9';
  if (s < 500) return '#f59e0b';
  return '#ef4444';
}
function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname.length > 1 ? u.host + u.pathname : u.host;
  } catch {
    return url.slice(0, 60);
  }
}

/**
 * A compact, real-time DevTools side panel for the Live page. Shows the
 * console/network/errors/perf streams accumulated since co-watching began.
 */
export function LiveDevTools({ data }: { data: InspectorData | null }) {
  const t = useT().replay.devTools;
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState<number | null>(null);
  const net = data?.network ?? [];
  const con = data?.console ?? [];
  const err = data?.errors ?? [];
  const perf = data?.perf ?? [];
  const last = perf[perf.length - 1];

  return (
    <Paper>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0 } }}>
        <Tab label={`${t.network}${net.length ? ` (${net.length})` : ''}`} />
        <Tab label={`${t.console}${con.length ? ` (${con.length})` : ''}`} />
        <Tab label={`${t.errors}${err.length ? ` (${err.length})` : ''}`} />
        <Tab label={t.perf} />
      </Tabs>
      <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
        {!data && <Empty>{t.waiting}</Empty>}

        {tab === 0 && data && (net.length === 0 ? <Empty>{t.noRequests}</Empty> : net.map((n, i) => {
          const isOpen = open === i;
          const detail = !!n.reqBody || !!n.resBody || !!n.reqHeaders || !!n.resHeaders || !!n.operationName;
          return (
            <Box key={i} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" spacing={1} alignItems="center" onClick={() => detail && setOpen(isOpen ? null : i)} sx={{ px: 1, py: 0.5, cursor: detail ? 'pointer' : 'default', '&:hover': detail ? { bgcolor: 'action.hover' } : undefined }}>
                <Chip label={n.method} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, minWidth: 40, bgcolor: 'action.selected', fontFamily: 'ui-monospace, monospace', '& .MuiChip-label': { px: 0.5 } }} />
                <Chip
                  label={n.status || (n.kind === 'resource' ? '—' : 'ERR')}
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: 9,
                    // A static resource with no readable status is unknown, not failed.
                    bgcolor: n.kind === 'resource' && !n.status ? 'action.selected' : statusColor(n.status),
                    color: n.kind === 'resource' && !n.status ? 'text.secondary' : '#fff',
                    minWidth: 34,
                  }}
                />
                {n.operationName && <Chip label={`gql`} size="small" sx={{ height: 16, fontSize: 9, bgcolor: '#e0335522', color: '#e11d48' }} />}
                <Box sx={{ flex: 1, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortUrl(n.url)}</Box>
                <Typography variant="caption" sx={{ color: n.slow ? '#f59e0b' : 'text.secondary' }}>{n.durationMs}ms</Typography>
              </Stack>
              {isOpen && (
                <Box sx={{ px: 1.5, pb: 1, bgcolor: 'action.hover', fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>
                  {n.operationName && <Detail label={t.graphql} value={`${n.operationType ?? ''} ${n.operationName}`} />}
                  {n.reqBody && <Detail label={t.request} value={n.reqBody} pre />}
                  {n.resBody && <Detail label={t.response} value={n.resBody} pre />}
                </Box>
              )}
            </Box>
          );
        }))}

        {tab === 1 && data && (con.length === 0 ? <Empty>{t.noConsole}</Empty> : con.map((c, i) => (
          <Box key={i} sx={{ px: 1, py: 0.4, borderBottom: '1px solid', borderColor: 'divider', fontSize: 11, fontFamily: 'ui-monospace, monospace', color: LEVEL_COLOR[c.level] ?? 'text.primary', wordBreak: 'break-word' }}>
            {c.message}
          </Box>
        )))}

        {tab === 2 && data && (err.length === 0 ? <Empty>{t.noErrors}</Empty> : err.map((e, i) => (
          <Box key={i} sx={{ px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, display: 'block' }}>{e.message}</Typography>
            {e.source && <Typography variant="caption" color="text.secondary">{shortUrl(e.source)}{e.line ? `:${e.line}` : ''}</Typography>}
          </Box>
        )))}

        {tab === 3 && data && (
          <Box sx={{ p: 1.5 }}>
            {!last ? <Typography variant="caption" color="text.secondary">No performance samples yet.</Typography> : (
              <Stack direction="row" spacing={3}>
                <Metric label={t.fps} value={last.fps != null ? String(last.fps) : '—'} tone={last.fps != null && last.fps < 30 ? '#f59e0b' : undefined} />
                <Metric label={t.domNodes} value={last.domNodes != null ? last.domNodes.toLocaleString() : '—'} />
                <Metric label={t.memory} value={last.memoryMB != null ? `${last.memoryMB} MB` : '—'} />
              </Stack>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <Typography sx={{ p: 1.5 }} variant="caption" color="text.secondary">{children}</Typography>;
}
function Detail({ label, value, pre }: { label: string; value: string; pre?: boolean }) {
  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 9, textTransform: 'uppercase' }}>{label}</Typography>
      <Box component={pre ? 'pre' : 'div'} sx={{ m: 0, mt: 0.25, maxHeight: 120, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary' }}>{value}</Box>
    </Box>
  );
}
function Metric({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography sx={{ fontWeight: 700, fontSize: 16, color: tone ?? 'text.primary' }}>{value}</Typography>
    </Box>
  );
}
